/**
 * AnalysisPanel
 * The report email's analysis, rendered in-app: one-line takeaway + action
 * badge, metric cards, returns-sensitivity matrix, common-size benchmark vs
 * our portfolio averages (issues/opportunities + "as presented vs as we'd run
 * it"), and the Claude narrative. Reads ai_import_jobs.report_analysis +
 * report_* columns via the shared job query — send-ai-import-report persists
 * them at send time for email jobs and via generate_only for app uploads.
 *
 * Presentation is delegated to AnalysisContent so the public share page renders
 * identical markup. When an onShare handler is supplied (the detail page) and a
 * structured analysis exists, a Share button appears in the header.
 *
 * Generation is async (the worker fires it after extraction completes), so the
 * panel shows a "generating" state while the job is extracted but the analysis
 * hasn't landed; useAIImportJob polls until it does.
 */

import { useEffect, useRef, useState } from 'react';
import { AlertCircle, AlertTriangle, Loader2, Share2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { isAnalysisTimedOut, useAIImportJob, useRegenerateAnalysis } from '@/hooks/useAIImport';
import type { UnderwritingScenario } from '@/services/ai-import.service';
import type { AIImportJob, ReportAnalysis } from '@/types/ai-import';
import { ActionBadge, AnalysisContent } from './AnalysisContent';

/** Extraction finished — analysis is either present or on its way. */
const ANALYSIS_ELIGIBLE: AIImportJob['status'][] = ['completed_pending_review', 'applied'];

const SCENARIOS: { key: UnderwritingScenario; label: string }[] = [
  { key: 'conservative', label: 'Conservative' },
  { key: 'base', label: 'Base' },
  { key: 'aggressive', label: 'Aggressive' },
];

/** Compact conservative/base/aggressive selector for the analysis header. */
function ScenarioToggle({
  current,
  pending,
  onSelect,
}: {
  current: UnderwritingScenario;
  pending: boolean;
  onSelect: (s: UnderwritingScenario) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Underwriting scenario"
      className="inline-flex items-center rounded-lg border border-gray-200 p-0.5 dark:border-gray-700"
    >
      {SCENARIOS.map((s) => {
        const active = s.key === current;
        return (
          <button
            key={s.key}
            type="button"
            disabled={pending}
            aria-pressed={active}
            onClick={() => !active && onSelect(s.key)}
            className={cn(
              'rounded-md px-2 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60',
              active
                ? 'bg-primary-500 text-white'
                : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800',
            )}
          >
            {s.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Self-contained: fetches the job (shared query cache with the rest of the
 * import UI) and renders nothing when the job has no analysis and none is
 * expected. Drop it into the Decision step or the import detail page. Pass
 * onShare to surface a Share button (only shown once a structured analysis
 * exists — that's the shareable payload). Pass `frameless` when the panel is
 * already set off by a host container (e.g. under a divider) to skip its own
 * border/padding and avoid nested frames.
 */
export function AnalysisPanel({
  jobId,
  onShare,
  frameless = false,
}: {
  jobId: string;
  onShare?: () => void;
  frameless?: boolean;
}) {
  const { data: job } = useAIImportJob(jobId);
  const regenerate = useRegenerateAnalysis();
  // Optimistically highlight the clicked scenario during the round-trip.
  const [pendingScenario, setPendingScenario] = useState<UnderwritingScenario | null>(null);
  const autoHealed = useRef(false);

  const analysis = (job?.report_analysis as ReportAnalysis | null) ?? null;
  const hasNarrative = !!(job?.report_narrative_html || job?.report_one_line_takeaway);
  const eligible = !!job && !analysis && !hasNarrative && ANALYSIS_ELIGIBLE.includes(job.status);
  // Generation is fire-and-forget and can silently never land; once past the
  // budget, show a terminal state instead of a spinner that runs forever.
  const timedOut = eligible && !!job && isAnalysisTimedOut(job);

  // Self-heal a dropped generate_only: if extraction finished but no analysis
  // landed and we're not past the budget, regenerate the FULL report once
  // (narrative + numbers) rather than stranding the user on a forever-spinner.
  useEffect(() => {
    if (eligible && !timedOut && !autoHealed.current && !regenerate.isPending) {
      autoHealed.current = true;
      regenerate.mutate({ id: jobId, scenario: 'base', scenarioOnly: false });
    }
  }, [eligible, timedOut, jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!regenerate.isPending) setPendingScenario(null);
  }, [regenerate.isPending]);

  if (!job) return null;

  const generating = eligible && !timedOut;
  if (!analysis && !hasNarrative && !eligible) return null;

  const scenario: UnderwritingScenario = pendingScenario ?? analysis?.assumptions?.scenario ?? 'base';

  return (
    <div className={cn('space-y-4', !frameless && 'rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900')}>
      <div className="flex flex-wrap items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary-600 dark:text-primary-400" />
        <span className="font-semibold text-gray-900 dark:text-gray-100">AI Analysis</span>
        <ActionBadge action={job.report_recommended_action} />
        <div className="ml-auto flex items-center gap-2">
          {analysis && (
            <>
              {regenerate.isPending && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
              <ScenarioToggle
                current={scenario}
                pending={regenerate.isPending}
                onSelect={(s) => {
                  setPendingScenario(s);
                  regenerate.mutate({ id: jobId, scenario: s });
                }}
              />
            </>
          )}
          {onShare && analysis && (
            <Button
              variant="outline"
              size="sm"
              onClick={onShare}
              leftIcon={<Share2 className="h-4 w-4" />}
            >
              Share
            </Button>
          )}
        </div>
      </div>

      {regenerate.isError && (
        <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="h-4 w-4" />
          Couldn’t regenerate the analysis. Please try again.
        </div>
      )}

      {generating && (
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Analysis generating — this usually takes under a minute…
        </div>
      )}

      {timedOut && (
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <AlertCircle className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          No analysis available — re-run this import with AI to generate one.
        </div>
      )}

      {/* The plausibility gate's verdict. The hold-notice email points here
          ("Review the full analysis in Flow"), so this page must say what was
          held and why — otherwise it presents the flagged figures as clean. */}
      {analysis?.plausibility && analysis.plausibility.implausible.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-900/20">
          <div className="flex items-center gap-2 font-semibold text-amber-800 dark:text-amber-300">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            {analysis.plausibility.implausible.every((f) => f.basis === 'flow_rerun')
              ? job.report_held_at
                ? 'Report held — Flow’s re-underwrite lands outside what an MHC can physically produce'
                : 'Flow’s re-underwrite lands outside what an MHC can physically produce'
              : job.report_held_at
                ? 'Report held — figures below are outside what an MHC can physically produce'
                : 'Impossible figures — outside what an MHC can physically produce'}
          </div>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-amber-800 dark:text-amber-200">
            {analysis.plausibility.implausible.map((f, i) => (
              <li key={i}>
                {f.property ? <span className="font-medium">{f.property} — </span> : null}
                {f.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <AnalysisContent
        oneLineTakeaway={job.report_one_line_takeaway}
        metrics={analysis?.metrics ?? null}
        returnsMatrix={analysis?.returns_matrix ?? null}
        commonSize={analysis?.common_size ?? null}
        commonSizeSkipReason={analysis?.common_size_skip_reason ?? null}
        leaseUp={analysis?.lease_up ?? null}
        assumptions={analysis?.assumptions ?? null}
        properties={analysis?.properties ?? null}
        salesComps={analysis?.sales_comps ?? null}
        threadOtherListings={analysis?.thread_other_listings ?? null}
        grouping={analysis?.grouping ?? null}
        narrativeHtml={job.report_narrative_html}
      />
    </div>
  );
}
